import React from 'react'
import PropTypes from 'prop-types'
import { Container, Columns, Column } from '@resurs/midas-react'
import { Conditional } from '@resurs/react-utils'
import { Tabs } from '../../../../midasComponents'
import { actOnData } from '../../../../util/helpers'
import { getPaymentMethodDescription } from '../../../../util/payApplyUtils'
import Frame from '../shared/Frame'
import DebitPayment from './DebitPayment'
import CreditLimit from './CreditLimit'
import CardSelector from './CardSelector'
import PaymentMethodHeader from './PaymentMethodHeader'
import PaymentMethodDescription from './PaymentMethodDescription'
import PaymentPerMonth from '../shared/PaymentPerMonth'
import { getText } from '../../../../internal/TextLabels'
import AccountInformation from '../shared/AccountInformation'
import BonusChecks from '../shared/BonusChecks/BonusChecks'
import { renderBonusLabel } from '../shared/BonusChecks/UIHelpers'
import {
  isExistingPartPayment,
  isRevolvingCredit,
} from '../../../../models/payment-methods'
import PendingAccountApplication from './PendingAccountApplication'

class ExistingCardOrAccount extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      creditValid: false,
      debitValid: false,
    }
  }

  static getDerivedStateFromProps(nextProps) {
    return {
      creditAvailable: nextProps.newCreditLimitChecked,
      debitAvailable: nextProps.debitPaymentChecked,
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.creditValid !== this.state.creditValid ||
      prevState.debitValid !== this.state.debitValid ||
      prevProps.newCreditLimitChecked !== this.props.newCreditLimitChecked ||
      prevProps.debitPaymentChecked !== this.props.debitPaymentChecked
    ) {
      if (this.props.debitPaymentChecked && this.props.newCreditLimitChecked) {
        this.props.isValid(
          this.state.creditValid &&
            this.state.debitValid &&
            this.props.amount > 0,
        )
      }
      if (this.props.debitPaymentChecked && !this.props.newCreditLimitChecked) {
        this.props.isValid(this.state.debitValid && this.props.amount > 0)
      }
      if (!this.props.debitPaymentChecked && this.props.newCreditLimitChecked) {
        this.props.isValid(this.state.creditValid)
      }
      if (
        !this.props.debitPaymentChecked &&
        !this.props.newCreditLimitChecked
      ) {
        this.props.isValid(false)
      }
    }
  }

  handleCreditLimitSwitch(checked) {
    if (checked) {
      this.setState({ creditValid: false })
    }
    this.props.onChangeLimit(0)
    this.props.onCreditLimitSwitch(checked)
  }

  handleDebitPaymentSwitch(checked) {
    if (checked) {
      this.setState({ debitValid: false })
    }
    this.props.onChangeAmount(0)
    this.props.onDebitPaymentSwitch(checked)
  }

  valid(validation) {
    if (this.props.debitPaymentChecked) {
      if (validation.key === 'debit-amount') {
        this.setState({ debitValid: validation.valid }, () =>
          this.props.isValid(this.validate()),
        )
      }
    }
    if (this.props.newCreditLimitChecked) {
      if (validation.key === 'limit') {
        this.setState({ creditValid: validation.valid }, () =>
          this.props.isValid(this.validate()),
        )
      }
    }
  }

  cardOrAccountNumberIsMasked() {
    return (
      this.props.selectedPaymentMethod.availableCards ||
      this.props.selectedPaymentMethod.availableAccounts
    ).includes('X')
  }

  validate() {
    if (!this.props.debitPaymentChecked && !this.props.newCreditLimitChecked) {
      return false
    }
    if (this.props.newCreditLimitChecked && !this.state.creditValid) {
      return false
    }
    return !(this.props.debitPaymentChecked && !this.state.debitValid)
  }

  render() {
    const {
      selectedPaymentMethod,
      limit,
      card,
      data,
      availableCards,
      updateCardSelected,
      newCreditLimitChecked,
      onChangeLimit,
      newPageLoading,
      debitPaymentChecked,
      onChangeAmount,
      onChangeDebitReference,
      debitReference,
      coApplicant,
      customerType,
      onChangeCard,
      selectedCard,
      getBasisConfirmationPdf,
      debitDisabled,
      paymentMethodsExist,
    } = this.props
    return (
      <Container>
        <Columns>
          <Column col12>
            <PaymentMethodHeader
              description={getPaymentMethodDescription(selectedPaymentMethod)}
            >
              <Conditional when={!isExistingPartPayment(selectedPaymentMethod)}>
                <PaymentPerMonth
                  paymentMethodsExist={paymentMethodsExist}
                  method={selectedPaymentMethod}
                  amount={limit}
                  customerType={customerType}
                />
              </Conditional>
            </PaymentMethodHeader>
          </Column>
          <Column col12>
            <Tabs
              fullWidth
              contentStyling={{ minHeight: '760px' }}
              values={[
                {
                  value: getText('payApply.paymentMethods.tabs.handle'),
                  active: true,
                  content: (
                    <>
                      {selectedPaymentMethod.accounts && (
                        <Frame>
                          <CardSelector
                            card={card}
                            availableCards={availableCards()}
                            onCardSelected={accountNbr => {
                              updateCardSelected(accountNbr)
                            }}
                            customerType={customerType}
                          />
                          <Conditional when={!!card.applicationPending}>
                            <PendingAccountApplication
                              key={card.accountNumber}
                              card={card}
                              payApplyData={data}
                            />
                          </Conditional>
                        </Frame>
                      )}
                      <Frame>
                        <CreditLimit
                          selectedPaymentMethod={selectedPaymentMethod}
                          limit={limit}
                          minApplicationLimit={
                            selectedPaymentMethod.minApplicationLimit >
                            card.approvedCredit
                              ? selectedPaymentMethod.minApplicationLimit
                              : card.approvedCredit + 1
                          }
                          maxApplicationLimit={
                            selectedPaymentMethod.maxApplicationLimit
                          }
                          disabled={
                            !selectedPaymentMethod.supportedActions.includes(
                              'LIMIT_RAISE',
                            ) ||
                            newPageLoading ||
                            !card.supportsLimitRaise
                          }
                          isValid={validation => this.valid(validation)}
                          card={card}
                          data={this.props.data}
                          newCreditLimitChecked={newCreditLimitChecked}
                          showCheckbox
                          onChangeLimit={newLimit => onChangeLimit(newLimit)}
                          onCreditLimitSwitch={checked =>
                            this.handleCreditLimitSwitch(checked)
                          }
                          coApplicant={coApplicant}
                        />
                      </Frame>
                      {!debitDisabled && (
                        <Frame>
                          {actOnData(
                            {
                              isCardNotDebitable:
                                ((card.cardNumber &&
                                  card.cardNumber.toUpperCase().includes('X') &&
                                  !card.allowPaymentWithoutCard) ||
                                  (card.allowPaymentWithoutCard != null &&
                                    !card.allowPaymentWithoutCard)) &&
                                !newCreditLimitChecked &&
                                (card.purchaseAllowed === undefined ||
                                  !!card.purchaseAllowed),
                            },
                            // eslint-disable-next-line react/no-unstable-nested-components
                            ({ isCardNotDebitable }) => (
                              <DebitPayment
                                disabled={
                                  isCardNotDebitable ||
                                  newPageLoading ||
                                  (newCreditLimitChecked &&
                                    card &&
                                    !!card.applicationPending)
                                }
                                debitPaymentChecked={
                                  !isCardNotDebitable && debitPaymentChecked
                                }
                                isValid={validation => this.valid(validation)}
                                onDebitPaymentSwitch={checked =>
                                  this.handleDebitPaymentSwitch(checked)
                                }
                                onChangeAmount={newAmount =>
                                  onChangeAmount(newAmount)
                                }
                                debitReference={debitReference}
                                onChangeDebitReference={ref =>
                                  onChangeDebitReference(ref)
                                }
                                minPurchaseLimit={
                                  selectedPaymentMethod.minPurchaseLimit
                                }
                                maxPurchaseLimit={
                                  newCreditLimitChecked &&
                                  limit > card.approvedCredit
                                    ? limit -
                                      (card.approvedCredit - card.usableAmount)
                                    : card.usableAmount
                                }
                                selectedCard={selectedCard}
                                availableCards={availableCards()}
                                onChangeCard={cardNumber =>
                                  onChangeCard(cardNumber)
                                }
                                selectedPaymentMethod={selectedPaymentMethod}
                                debitUnavailableReason={
                                  (isCardNotDebitable &&
                                    getText(
                                      'payApply.paymentMethod.debitInTerminalRequired',
                                    )) ||
                                  ''
                                }
                              />
                            ),
                          )}
                        </Frame>
                      )}
                    </>
                  ),
                },
                {
                  value: getText('payApply.paymentMethods.tabs.info'),
                  content: (
                    <div style={{ marginTop: '2rem' }}>
                      <PaymentMethodDescription
                        method={selectedPaymentMethod}
                      />
                    </div>
                  ),
                },
                {
                  value: isRevolvingCredit(selectedPaymentMethod.type)
                    ? getText('payApply.paymentMethods.tabs.codesAccount')
                    : getText('payApply.paymentMethods.tabs.codesCard'),
                  content: (
                    <AccountInformation
                      selectedCard={selectedCard}
                      availableCards={availableCards()}
                      customerType={selectedPaymentMethod.customerType}
                      onChangeCard={cardNumber => onChangeCard(cardNumber)}
                      getBasisConfirmationPdf={pdfData =>
                        getBasisConfirmationPdf(pdfData)
                      }
                    />
                  ),
                  disabled: this.cardOrAccountNumberIsMasked(),
                },
                {
                  value: renderBonusLabel(this.props.bonusChecks),
                  content: <BonusChecks />,
                  disabled: !this.props.hasBonusSupport,
                },
              ]}
            />
          </Column>
        </Columns>
      </Container>
    )
  }
}

ExistingCardOrAccount.propTypes = {
  amount: PropTypes.any.isRequired,
  selectedPaymentMethod: PropTypes.object.isRequired,
  limit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  newPageLoading: PropTypes.bool.isRequired,
  card: PropTypes.object,
  data: PropTypes.object.isRequired,
  isValid: PropTypes.func.isRequired,
  onChangeLimit: PropTypes.func.isRequired,
  onDebitPaymentSwitch: PropTypes.func.isRequired,
  debitPaymentChecked: PropTypes.bool.isRequired,
  onChangeAmount: PropTypes.func.isRequired,
  debitReference: PropTypes.string,
  onChangeDebitReference: PropTypes.func.isRequired,
  availableCards: PropTypes.func.isRequired,
  updateCardSelected: PropTypes.func.isRequired,
  newCreditLimitChecked: PropTypes.bool.isRequired,
  onCreditLimitSwitch: PropTypes.func.isRequired,
  coApplicant: PropTypes.object,
  customerType: PropTypes.string.isRequired,
  hasBonusSupport: PropTypes.bool.isRequired,
  bonusChecks: PropTypes.array.isRequired,
  onChangeCard: PropTypes.func.isRequired,
  selectedCard: PropTypes.object,
  getBasisConfirmationPdf: PropTypes.func.isRequired,
  debitDisabled: PropTypes.bool.isRequired,
  paymentMethodsExist: PropTypes.bool,
}

ExistingCardOrAccount.defaultProps = {
  limit: null,
  card: null,
  coApplicant: null,
  debitReference: null,
  selectedCard: null,
  paymentMethodsExist: false,
}

export default ExistingCardOrAccount
